import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getSidebarItems } from './config/SidebarItems';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getBookmarks, getCachedBookmarks, bookmarkToMenuItem } from '../../utils/bookmarks';
import BookmarksManager from './BookmarksManager';
import { CogIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, ChevronDownIcon, StarIcon } from '../ui/Icons';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { FlyoutRailBackdrop, FlyoutRailPanel } from '../ui/FlyoutFromRail';
import api from '../../services/api';
import useSWR from 'swr';
import { useUserSettings } from '../../modules/admin/settings/useData';
import { useModuleEntitlements } from '../../modules/admin/module-management';
import { useEffectiveSidebarLayout } from '../../hooks/useEffectiveSidebarLayout';
import { SidebarExpandedContext, sidebarGroupExpandKey, useSidebarExpanded } from './SidebarExpandedContext';

// Helper functions for view preferences using API
const viewPreferencesFetcher = async (url: string) => {
  const response = await api.get(url);
  return response.data.data || {};
};

const getViewPreference = (moduleName: string, preferences: Record<string, string> | null): string | null => {
  if (!preferences) return null;
  return preferences[moduleName] || null;
};

const saveViewPreference = async (moduleName: string, viewType: string, mutate: () => Promise<any>): Promise<void> => {
  try {
    await api.patch('/settings/user/view-preferences', {
      preferences: {
        [moduleName]: viewType,
      },
    });
    // Refresh the preferences cache
    await mutate();
  } catch (e) {
    console.error('Failed to save view preference:', e);
  }
};

export interface MenuItemView {
  type: 'list' | 'kanban' | 'card' | 'calendar' | 'table' | 'templates' | 'grouped' | 'tree' | 'summary' | 'daily' | 'daily_2' | 'analytics' | 'categories' | 'yearly' | 'monthly';
  label: string;
  path: string;
  icon: React.ReactNode;
}

export interface MenuItem {
  name: string;
  path?: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  views?: MenuItemView[];
  badge?: number;
  /** Optional grouping key (e.g. for module-management UI); sidebar visibility uses permissions only. */
  moduleGroupKey?: string;
  /** When false, group starts collapsed (still auto-expands when a child route is active). */
  defaultExpanded?: boolean;
  /** Permission module key for view check; if not set, derived from path when filtering by permissions. */
  permissionModuleKey?: string;
  /** Non-clickable section divider label (e.g. Work, Reference, System). */
  sectionLabel?: string;
  /** Child entry rendered under Bookmarks (shows bookmark styling). */
  isBookmark?: boolean;
}

function bookmarkItemDecorations(item: MenuItem): {
  leadingIcon: React.ReactNode | null;
  trailingMark: React.ReactNode | null;
} {
  const hasModuleIcon = !!item.icon;
  const leadingIcon =
    item.icon ??
    (item.isBookmark ? <StarIcon className="w-5 h-5 text-yellow-500" filled /> : null);
  const trailingMark =
    item.isBookmark && hasModuleIcon ? (
      <StarIcon className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" filled />
    ) : null;
  return { leadingIcon, trailingMark };
}

// Toggle View Button Component
interface ToggleViewButtonProps {
  views: MenuItemView[];
  currentPath: string;
  onViewChange: (path: string) => Promise<void>;
  colors: any;
  collapsed?: boolean;
  moduleName: string;
  viewPreferences?: Record<string, string>;
  mutateViewPreferences?: () => Promise<any>;
}

const ToggleViewButton: React.FC<ToggleViewButtonProps> = ({
  views,
  currentPath,
  onViewChange,
  colors,
  collapsed = false,
  moduleName,
  viewPreferences = {},
  mutateViewPreferences,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Find current view based on path, or use saved preference
  const getCurrentView = () => {
    const savedViewType = getViewPreference(moduleName, viewPreferences);
    const exactPathMatch = views.find(v => currentPath === v.path);
    const pathMatch = views.find(v =>
      currentPath.startsWith(v.path + '/')
    );

    // PRIORITY 1: If we're on a view's exact path (not a detail page), that view is current so only it gets the tick
    if (exactPathMatch && !pathMatch) {
      return exactPathMatch;
    }

    // PRIORITY 2: Saved preference (for detail pages, dashboard, or when path doesn't match a view)
    if (savedViewType) {
      const savedView = views.find(v => v.type === savedViewType);
      if (savedView) {
        return savedView;
      }
    }

    // PRIORITY 3: Path prefix match (e.g. detail page under a view)
    if (pathMatch) {
      return pathMatch;
    }

    return views[0];
  };

  const currentView = getCurrentView();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (collapsed) {
    return (
      <button
        onClick={async (e) => {
          e.stopPropagation();
          if (views.length > 1 && mutateViewPreferences) {
            // Cycle through views
            const currentIndex = views.findIndex(v => v === currentView);
            const nextIndex = (currentIndex + 1) % views.length;
            const nextView = views[nextIndex];
            // Save preference when cycling
            await saveViewPreference(moduleName, nextView.type, mutateViewPreferences);
            await onViewChange(nextView.path);
          }
        }}
        className={cn(
          'p-1.5 rounded-lg transition-colors',
          colors.sidebarText,
          colors.sidebarItemHover
        )}
        title={`Switch view (${currentView.label})`}
      >
        {currentView.icon}
      </button>
    );
  }

  if (views.length <= 1) {
    return null;
  }

  return (
    <div className="relative" ref={buttonRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors text-xs',
          colors.sidebarText,
          colors.sidebarItemHover
        )}
        title={currentView.label}
      >
        <span className="w-4 h-4 flex items-center justify-center">
          {currentView.icon}
        </span>
        <ChevronDownIcon className={cn('w-3 h-3 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute right-0 mt-1 w-48 rounded-lg shadow-lg border',
            colors.sidebarBackground,
            colors.border
          )}
          style={{ top: '100%', ...zStyle(Z_INDEX.localDropdown) }}
        >
          <div className="py-1">
            {views.map((view, index) => {
              // Compare by path so only the current view shows the tick (e.g. Monthly View vs Monthly Report)
              const isActive = view.path === currentView.path;
              return (
                <button
                  key={index}
                  onClick={async (e) => {
                    e.stopPropagation();
                    // Save preference when selecting a view
                    if (mutateViewPreferences) {
                      await saveViewPreference(moduleName, view.type, mutateViewPreferences);
                    }
                    await onViewChange(view.path);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                    isActive
                      ? `${colors.sidebarActive} ${colors.sidebarActiveText}`
                      : `${colors.sidebarText} ${colors.sidebarItemHover}`
                  )}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    {view.icon}
                  </span>
                  <span className="flex-1 text-left">{view.label}</span>
                  {isActive && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

interface MenuItemComponentProps {
  item: MenuItem;
  level: number;
  location: ReturnType<typeof useLocation>;
  isMobileOpen: boolean;
  setIsMobileOpen: (open: boolean) => void;
  isBookmarks?: boolean;
  onManageBookmarks?: () => void;
  collapsed?: boolean;
  onItemClick?: (item: MenuItem, hasSubitems: boolean) => void;
  viewPreferences?: Record<string, string>;
  mutateViewPreferences?: () => Promise<any>;
  /** Parent expand key chain for nested group persistence */
  parentExpandKey?: string;
  /** When set, only this item is active if it is the best match (longest path); prevents parent path from being active when a child path matches. */
  activePathAmongSiblings?: string;
}

/** True if pathname matches this item's route or any descendant's route (nested sidebar). */
function menuItemOrDescendantMatchesPath(item: MenuItem, pathname: string): boolean {
  if (item.path && (pathname === item.path || pathname.startsWith(item.path + '/'))) {
    return true;
  }
  if (item.views?.some((v) => v.path && (pathname === v.path || pathname.startsWith(v.path + '/')))) {
    return true;
  }
  if (item.children?.length) {
    return item.children.some((c) => menuItemOrDescendantMatchesPath(c, pathname));
  }
  return false;
}

function collectMenuPaths(item: MenuItem, out: string[]): void {
  if (item.path) out.push(item.path);
  item.views?.forEach((v) => {
    if (v.path) out.push(v.path);
  });
  item.children?.forEach((c) => collectMenuPaths(c, out));
}

const MenuItemComponent: React.FC<MenuItemComponentProps> = ({
  item,
  level,
  location,
  isMobileOpen,
  setIsMobileOpen,
  isBookmarks = false,
  onManageBookmarks,
  collapsed = false,
  onItemClick,
  viewPreferences = {},
  mutateViewPreferences,
  activePathAmongSiblings,
  parentExpandKey,
}) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const sidebarExpanded = useSidebarExpanded();
  const hasChildren = item.children && item.children.length > 0;
  const hasViews = item.views && item.views.length > 0;
  const expandKey =
    hasChildren && !hasViews ? sidebarGroupExpandKey(item.name, parentExpandKey) : undefined;
  const savedExpanded = expandKey ? sidebarExpanded?.getExpanded(expandKey) : undefined;
  const [isExpanded, setIsExpanded] = useState(
    savedExpanded ?? (item.defaultExpanded === false ? false : (isBookmarks && hasChildren)),
  );
  
  // Get preferred view path or default to first view
  const getPreferredViewPath = (): string | undefined => {
    if (!hasViews || !item.views) return undefined;
    const savedViewType = getViewPreference(item.name, viewPreferences);
    if (savedViewType) {
      // Find view by type and return its path
      const savedView = item.views.find(view => view.type === savedViewType);
      if (savedView) {
        return savedView.path;
      }
    }
    // Default to first view
    return item.views[0].path;
  };
  
  // Determine current path - use preferred view path if no direct path
  const itemPath = item.path || (hasViews ? getPreferredViewPath() : undefined);
  
  // Check if current path or any child path is active; when activePathAmongSiblings is set, only active if this item is the best match (so parent path like /settings is not active when /settings/offer-pdf-layouts is active)
  const pathMatches = itemPath
    ? location.pathname === itemPath || location.pathname.startsWith(itemPath + '/')
    : false;
  const isActive = pathMatches && (
    !activePathAmongSiblings ||
    activePathAmongSiblings === itemPath ||
    (hasViews && item.views!.some((v) => activePathAmongSiblings === v.path || (v.path && activePathAmongSiblings.startsWith(v.path + '/'))))
  );
  
  // Check if any child is active (direct path, views, or nested children e.g. Administration → Import Actions → IMPA Code)
  const hasActiveChild = hasChildren
    ? item.children!.some((child) => menuItemOrDescendantMatchesPath(child, location.pathname))
    : false;
  
  // Check if any view is active
  const hasActiveView = hasViews
    ? item.views!.some((view) => {
        return location.pathname === view.path || location.pathname.startsWith(view.path + '/');
      })
    : false;

  React.useEffect(() => {
    if (savedExpanded !== undefined) {
      setIsExpanded(savedExpanded);
    }
  }, [savedExpanded]);

  // Auto-expand if has active child or if it's Bookmarks with children (use startTransition to avoid freezing when expanding large trees)
  React.useEffect(() => {
    if (hasActiveChild) {
      React.startTransition(() => setIsExpanded(true));
    } else if (isBookmarks && hasChildren) {
      React.startTransition(() => setIsExpanded(true));
    }
  }, [hasActiveChild, isBookmarks, hasChildren]);

  // Check if we're on the base path (not a specific view) and should redirect to preferred view
  // This hook must be called unconditionally (React rules) - placed here before any conditional returns
  React.useEffect(() => {
    if (hasViews && item.views && item.path) {
      const savedViewType = getViewPreference(item.name, viewPreferences);
      let preferredPath: string | undefined;
      
      if (savedViewType) {
        // Find view by type and return its path
        const savedView = item.views.find(view => view.type === savedViewType);
        preferredPath = savedView?.path;
      } else {
        // Default to first view if no preference
        preferredPath = item.views[0]?.path;
      }
      
      // Only redirect when on the base path and current path is not already a view path.
      const currentPathIsAViewPath = item.views.some((v) => v.path === location.pathname);
      if (preferredPath && location.pathname === item.path && preferredPath !== item.path && !currentPathIsAViewPath) {
        // We're on the base path but have a preferred view - navigate to it
        navigate(preferredPath, { replace: true });
      }
    }
  }, [hasViews, item.views, item.path, item.name, location.pathname, viewPreferences, navigate]);

  // Handle view toggle
  const handleViewChange = async (path: string) => {
    // Save the view preference (find view by path and save its type)
    if (hasViews && item.views && mutateViewPreferences) {
      const view = item.views.find(v => v.path === path);
      if (view) {
        await saveViewPreference(item.name, view.type, mutateViewPreferences);
      }
    }
    navigate(path);
    if (isMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  const paddingLeft = collapsed ? 0 : level * 16 + 16;

  if (item.sectionLabel) {
    if (collapsed) return null;
    return (
      <div className={cn('pt-4 pb-1', level === 0 && 'first:pt-0')}>
        <span
          className={cn('px-4 text-[10px] font-bold uppercase tracking-widest', colors.textMuted)}
          style={level > 0 ? { paddingLeft: `${paddingLeft}px` } : undefined}
        >
          {item.sectionLabel}
        </span>
      </div>
    );
  }

  const handleClick = (e: React.MouseEvent) => {
    if (collapsed && hasChildren) {
      e.preventDefault();
      e.stopPropagation();
      if (onItemClick) {
        onItemClick(item, true);
      }
    } else if (hasChildren) {
      e.preventDefault();
      const next = !isExpanded;
      React.startTransition(() => {
        setIsExpanded(next);
        if (expandKey) sidebarExpanded?.setExpanded(expandKey, next);
      });
    }
  };

  // For Bookmarks section, always show as expandable (even if empty)
  // For items with views, show as regular item with toggle button
  const shouldShowAsExpandable = (hasChildren || isBookmarks) && !hasViews;

  if (shouldShowAsExpandable) {
    return (
      <div>
        <div className="flex items-center">
          {collapsed ? (
            <button
              onClick={handleClick}
              className={cn(
                'w-full flex items-center justify-center p-3 rounded-lg transition-all duration-200 relative group',
                isActive
                  ? `${colors.sidebarActive} ${colors.sidebarActiveText} shadow-lg`
                  : `${colors.sidebarText} ${colors.sidebarItemHover}`
              )}
              title={item.name}
            >
              {item.icon && (
                <span className={cn('flex items-center justify-center', isActive ? colors.sidebarActiveText : colors.sidebarText)}>
                  {item.icon}
                </span>
              )}
              {item.badge && (
                <span className="absolute top-1 right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </button>
          ) : (
            <>
              <button
                onClick={handleClick}
                className={cn(
                  'flex-1 flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                  isActive
                    ? `${colors.sidebarActive} ${colors.sidebarActiveText} shadow-lg`
                    : `${colors.sidebarText} ${colors.sidebarItemHover}`
                )}
                style={{ paddingLeft: `${paddingLeft}px` }}
              >
                {item.icon && (
                  <span className={cn('mr-3', isActive ? colors.sidebarActiveText : colors.sidebarText)}>
                    {item.icon}
                  </span>
                )}
                <span className="flex-1 text-left">{item.name}</span>
                {item.badge && (
                  <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
                <svg
                  className={cn('w-4 h-4 transition-transform duration-200 ml-2', isExpanded ? 'rotate-90' : '')}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {isBookmarks && onManageBookmarks && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onManageBookmarks();
                  }}
                  className={cn('p-2 mr-2 rounded-lg transition-colors', colors.sidebarText, colors.sidebarItemHover)}
                  title="Manage Bookmarks"
                >
                  <CogIcon className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </div>
        {!collapsed && isExpanded && (
          <div className="mt-1 space-y-1">
            {hasChildren && item.children!.length > 0 ? (() => {
              const pathname = location.pathname;
              const siblingPaths: string[] = [];
              item.children!.forEach((child) => {
                collectMenuPaths(child, siblingPaths);
              });
              const bestMatch = siblingPaths
                .filter((p) => pathname === p || pathname.startsWith(p + '/'))
                .sort((a, b) => b.length - a.length)[0];
              return item.children!.map((child, index) => (
                <MenuItemComponent
                  key={child.path || `${item.name}-child-${index}`}
                  item={child}
                  level={level + 1}
                  location={location}
                  isMobileOpen={isMobileOpen}
                  setIsMobileOpen={setIsMobileOpen}
                  collapsed={collapsed}
                  onItemClick={onItemClick}
                  viewPreferences={viewPreferences}
                  mutateViewPreferences={mutateViewPreferences}
                  activePathAmongSiblings={bestMatch}
                  parentExpandKey={expandKey}
                />
              ));
            })() : isBookmarks ? (
              <div className={cn('px-4 py-2 text-xs', colors.textSecondary)} style={{ paddingLeft: `${paddingLeft + 16}px` }}>
                No bookmarks yet. Click the settings icon to add some.
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="relative group">
        <button
          onClick={() => {
            if (hasViews && item.views) {
              // Navigate to preferred view (from user preferences) or first view
              const preferredPath = getPreferredViewPath();
              navigate(preferredPath || item.views[0].path);
            } else if (onItemClick) {
              onItemClick(item, false);
            }
          }}
          className={cn(
            'w-full flex items-center justify-center p-3 rounded-lg transition-all duration-200 relative',
            isActive || hasActiveView
              ? `${colors.sidebarActive} ${colors.sidebarActiveText} shadow-lg`
              : `${colors.sidebarText} ${colors.sidebarItemHover}`
          )}
          title={item.name}
        >
          {item.icon && (
            <span className={cn('flex items-center justify-center', isActive || hasActiveView ? colors.sidebarActiveText : colors.sidebarText)}>
              {item.icon}
            </span>
          )}
          {item.badge && (
            <span className="absolute top-1 right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {item.badge > 9 ? '9+' : item.badge}
            </span>
          )}
        </button>
        {hasViews && item.views && item.views.length > 1 && (
          <div
            className="absolute left-full top-0 ml-1 opacity-0 invisible pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto"
            style={zStyle(Z_INDEX.shellFlyoutNested)}
          >
            <div
              className={cn(
                'min-w-[11rem] rounded-lg border py-1 shadow-2xl backdrop-blur-sm',
                colors.sidebarBackground,
                colors.border
              )}
            >
              <p className={cn('px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider', colors.sidebarText)}>
                Views
              </p>
              {item.views.map((view, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleViewChange(view.path);
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md whitespace-nowrap transition-colors',
                    location.pathname === view.path || location.pathname.startsWith(view.path + '/')
                      ? `${colors.sidebarActive} ${colors.sidebarActiveText}`
                      : `${colors.sidebarText} ${colors.sidebarItemHover}`
                  )}
                >
                  <span className="w-4 h-4 flex-shrink-0">{view.icon}</span>
                  {view.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Item with views - show toggle button
  if (hasViews && item.views) {
    const preferredPath = getPreferredViewPath();
    const defaultPath = preferredPath || item.views[0].path;
    
    return (
      <div
        className={cn(
          'flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 relative group',
          isActive || hasActiveView
            ? `${colors.sidebarActive} ${colors.sidebarActiveText} shadow-lg`
            : `${colors.sidebarText} ${colors.sidebarItemHover}`
        )}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        <button
          onClick={async () => {
            // Always navigate to preferred path (from user preferences) or default to first view
            await handleViewChange(defaultPath);
            setIsMobileOpen(false);
          }}
          className="flex items-center flex-1 text-left w-full"
        >
          {item.icon && (
            <span className={cn('mr-3', isActive || hasActiveView ? colors.sidebarActiveText : colors.sidebarText)}>
              {item.icon}
            </span>
          )}
          <span className="flex-1">{item.name}</span>
        </button>
        {item.badge && (
          <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
            {item.badge > 9 ? '9+' : item.badge}
          </span>
        )}
        <ToggleViewButton
          views={item.views}
          currentPath={location.pathname}
          onViewChange={handleViewChange}
          colors={colors}
          moduleName={item.name}
          viewPreferences={viewPreferences}
          mutateViewPreferences={mutateViewPreferences}
        />
      </div>
    );
  }

  // Regular item without views
  const { leadingIcon, trailingMark } = bookmarkItemDecorations(item);

  return (
    <Link
      to={itemPath || '#'}
      onClick={() => setIsMobileOpen(false)}
      className={cn(
        'flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 relative',
        isActive
          ? `${colors.sidebarActive} ${colors.sidebarActiveText} shadow-lg`
          : `${colors.sidebarText} ${colors.sidebarItemHover}`
      )}
      style={{ paddingLeft: `${paddingLeft}px` }}
    >
      {leadingIcon && (
        <span className={cn('mr-3', isActive ? colors.sidebarActiveText : colors.sidebarText)}>
          {leadingIcon}
        </span>
      )}
      <span className="flex-1">{item.name}</span>
      {trailingMark}
      {item.badge && (
        <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
          {item.badge > 9 ? '9+' : item.badge}
        </span>
      )}
    </Link>
  );
};

/** Flyout width for collapsed app sidebar (matches email module rail panel ~20rem) */
const APP_SIDEBAR_FLYOUT_WIDTH = 288;

// Drawer component for collapsed mode (portal + theme aligned with email sidebar flyout)
interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem;
  level: 'level2' | 'level3' | 'level4';
  leftPosition: number;
  onItemClick?: (item: MenuItem, hasSubitems: boolean) => void;
  onBack?: () => void;
  showBack?: boolean;
  viewPreferences?: Record<string, string>;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  item,
  level: _drawerLevel,
  leftPosition,
  onItemClick,
  onBack,
  showBack = false,
  viewPreferences = {},
}) => {
  const { colors } = useTheme();
  const isMobile = false;
  const location = useLocation();
  const navigate = useNavigate();

  if (!isOpen || !item.children || item.children.length === 0) {
    return null;
  }

  const isCurrentPath = (href?: string) => {
    if (!href) return false;
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const getPreferredViewPath = (menuItem: MenuItem): string | undefined => {
    if (!menuItem.views || menuItem.views.length === 0) return menuItem.path;
    const savedViewType = getViewPreference(menuItem.name, viewPreferences);
    if (savedViewType) {
      const savedView = menuItem.views.find(view => view.type === savedViewType);
      if (savedView) {
        return savedView.path;
      }
    }
    return menuItem.views[0].path;
  };

  const panelMaxW = `min(${APP_SIDEBAR_FLYOUT_WIDTH}px, calc(100vw - ${leftPosition}px - 8px))`;

  const drawerTree = (
    <>
      <FlyoutRailBackdrop onClose={onClose} />
      <FlyoutRailPanel
        left={leftPosition}
        ariaLabel={item.name}
        className={cn(
          'transition-all duration-200 ease-out',
          colors.sidebarBackground,
          colors.border
        )}
        panelStyle={{
          width: isMobile ? `min(${APP_SIDEBAR_FLYOUT_WIDTH}px, 85vw)` : panelMaxW,
        }}
      >
        <div
          className={cn(
            'flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b min-h-[2.25rem]',
            colors.border
          )}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {showBack && onBack && (
              <button
                type="button"
                onClick={onBack}
                className={cn(
                  'flex-shrink-0 rounded-md p-1.5 transition-colors',
                  colors.sidebarText,
                  colors.sidebarItemHover
                )}
                title="Back"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
            )}
            {item.icon && (
              <span className={cn('flex-shrink-0 flex items-center justify-center', colors.sidebarText)}>
                {item.icon}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.sidebarText)}>
                Menu
              </p>
              <p className={cn('text-sm font-medium truncate', colors.sidebarTextHover)}>{item.name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'flex-shrink-0 rounded-md p-1.5 text-lg leading-none transition-colors',
              colors.sidebarText,
              colors.sidebarItemHover
            )}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        <div className={cn('flex-1 min-h-0 overflow-y-auto py-1', colors.sidebarBackground)}>
          <div className="space-y-0.5 px-2">
            {item.children.map((subitem, index) => {
              const hasSubitems = subitem.children && subitem.children.length > 0;
              const isActive = isCurrentPath(subitem.path);

              if (hasSubitems) {
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      onItemClick?.(subitem, true);
                    }}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm rounded-md transition-colors',
                      colors.sidebarText,
                      colors.sidebarItemHover
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {subitem.icon ? (
                        <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                          {subitem.icon}
                        </span>
                      ) : null}
                      <span className="font-medium truncate">{subitem.name}</span>
                    </div>
                    <ChevronRightIcon className="w-4 h-4 flex-shrink-0 opacity-70" />
                  </button>
                );
              }

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    const preferredPath = getPreferredViewPath(subitem);
                    if (preferredPath) {
                      navigate(preferredPath);
                      onClose();
                    } else if (subitem.path) {
                      navigate(subitem.path);
                      onClose();
                    }
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md transition-colors',
                    isActive
                      ? `${colors.sidebarActive} ${colors.sidebarActiveText}`
                      : `${colors.sidebarText} ${colors.sidebarItemHover}`
                  )}
                >
                  {(() => {
                    const { leadingIcon, trailingMark } = bookmarkItemDecorations(subitem);
                    return (
                      <>
                        {leadingIcon ? (
                          <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                            {leadingIcon}
                          </span>
                        ) : null}
                        <span className="font-medium flex-1 truncate">{subitem.name}</span>
                        {trailingMark}
                      </>
                    );
                  })()}
                  {subitem.badge && (
                    <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full flex-shrink-0">
                      {subitem.badge > 9 ? '9+' : subitem.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </FlyoutRailPanel>
    </>
  );

  return typeof document !== 'undefined' ? createPortal(drawerTree, document.body) : null;
};

interface SidebarProps {
  isMobileOpen: boolean;
  setIsMobileOpen: (open: boolean) => void;
}

// Valid view types for each module (must match backend)
const validViewTypes: Record<string, string[]> = {
  'Dashboard': ['list'],
};

// Validate view preferences - backend now returns all preferences with defaults
const validateViewPreferences = (prefs: Record<string, string> | null): Record<string, string> => {
  if (!prefs) {
    // If no preferences, return defaults (backend should have set these on login)
    const defaults: Record<string, string> = {};
    for (const [moduleName, validTypes] of Object.entries(validViewTypes)) {
      if (validTypes.length > 0) {
        defaults[moduleName] = validTypes[0];
      }
    }
    return defaults;
  }
  
  // Backend returns all preferences with defaults, so use as-is
  // Only filter out invalid values as a safety check
  const validated: Record<string, string> = {};
  for (const [moduleName, value] of Object.entries(prefs)) {
    const validTypes = validViewTypes[moduleName];
    if (validTypes && validTypes.includes(value)) {
      validated[moduleName] = value;
    } else if (validTypes && validTypes.length > 0) {
      // If invalid, use default (backend should have fixed this, but safety check)
      validated[moduleName] = validTypes[0];
    }
  }
  
  return validated;
};

const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen, setIsMobileOpen }) => {
  const { colors, sidebarCollapsed, setSidebarCollapsed } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarStyle = useEffectiveSidebarLayout();
  const isRail = sidebarStyle === 'rail';
  const isTabs = sidebarStyle === 'tabs';
  const isWorkspaces = sidebarStyle === 'workspaces';
  const collapsed = isRail ? true : sidebarCollapsed;
  const asideRef = useRef<HTMLElement | null>(null);
  const [railRightPx, setRailRightPx] = useState(64);
  const [bookmarksManagerOpen, setBookmarksManagerOpen] = useState(false);
  
  // Get user settings to check bookmarks display mode + sidebar expand memory
  const { userSettings, mutate: mutateUserSettings } = useUserSettings();
  const showBookmarksInSidebar = !userSettings?.bookmarksDisplayMode || userSettings.bookmarksDisplayMode === 'sidebar';

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    () => userSettings?.sidebarExpandedGroups ?? {},
  );
  const expandedPersistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (userSettings?.sidebarExpandedGroups) {
      setExpandedGroups(userSettings.sidebarExpandedGroups);
    }
  }, [userSettings?.sidebarExpandedGroups]);

  useEffect(() => {
    const onSidebarPrefs = () => {
      mutateUserSettings();
    };
    window.addEventListener('sidebarPreferencesUpdated', onSidebarPrefs);
    return () => window.removeEventListener('sidebarPreferencesUpdated', onSidebarPrefs);
  }, [mutateUserSettings]);

  const expandedGroupsRef = useRef(expandedGroups);
  expandedGroupsRef.current = expandedGroups;

  const sidebarExpandedValue = useMemo(
    () => ({
      getExpanded: (key: string) => expandedGroupsRef.current[key],
      setExpanded: (key: string, expanded: boolean) => {
        setExpandedGroups((prev) => {
          const next = { ...prev, [key]: expanded };
          if (expandedPersistTimer.current) clearTimeout(expandedPersistTimer.current);
          expandedPersistTimer.current = setTimeout(() => {
            api.patch('/settings/user', { sidebarExpandedGroups: next }).catch(() => undefined);
          }, 500);
          return next;
        });
      },
    }),
    [],
  );
  
  // Drawer state for collapsed mode
  const [activeDrawer, setActiveDrawer] = useState<'level2' | 'level3' | 'level4' | null>(null);
  const [drawerData, setDrawerData] = useState<{
    level2: MenuItem | null;
    level3: MenuItem | null;
    level4: MenuItem | null;
  }>({
    level2: null,
    level3: null,
    level4: null,
  });
  const [drawerHistory, setDrawerHistory] = useState<Array<{ level: string; data: MenuItem }>>([]);

  useLayoutEffect(() => {
    if (!collapsed) return;
    const update = () => {
      const el = asideRef.current;
      setRailRightPx(el ? el.getBoundingClientRect().right : 64);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [collapsed, activeDrawer]);

  // Fetch view preferences from API - revalidate on mount to ensure fresh data after login
  const { data: rawViewPreferences, mutate: mutateViewPreferences } = useSWR<Record<string, string>>(
    '/settings/user/view-preferences',
    viewPreferencesFetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      revalidateOnMount: true, // Always revalidate when component mounts (e.g., after login)
      refreshInterval: 0, // Don't auto-refresh, but allow manual refresh
    }
  );

  // Validate view preferences
  const viewPreferences = React.useMemo(() => {
    return validateViewPreferences(rawViewPreferences || null);
  }, [rawViewPreferences]);

  // Listen for view preferences updates from Preferences page and login
  React.useEffect(() => {
    const handleViewPreferencesUpdate = () => {
      // Force revalidation of view preferences
      mutateViewPreferences();
    };

    window.addEventListener('viewPreferencesUpdated', handleViewPreferencesUpdate);
    
    // Also listen for authentication state changes to refresh preferences after login
    const handleAuthChange = () => {
      // Small delay to ensure backend validation has completed
      setTimeout(() => {
        mutateViewPreferences();
      }, 500);
    };
    
    window.addEventListener('userAuthenticated', handleAuthChange);
    
    return () => {
      window.removeEventListener('viewPreferencesUpdated', handleViewPreferencesUpdate);
      window.removeEventListener('userAuthenticated', handleAuthChange);
    };
  }, [mutateViewPreferences]);
  
  const { user, can, permissionsLoaded } = useAuth();
  const { entitlements } = useModuleEntitlements();
  const vesselAccountId = useAccountVesselScope();
  const canView = useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded, user?.role]
  );
  const baseMenuItems = useMemo(
    () => getSidebarItems(canView, vesselAccountId, entitlements),
    [canView, vesselAccountId, entitlements]
  );

  // Stable key so effect only runs when menu structure actually changes (avoids infinite loop from new baseMenuItems reference every render)
  const baseMenuItemsKey = useMemo(
    () => JSON.stringify(baseMenuItems.map((i) => ({ name: i.name, path: i.path, childCount: i.children?.length ?? 0 }))),
    [baseMenuItems]
  );

  // Initialize with base menu items
  const [menuItemsWithBookmarks, setMenuItemsWithBookmarks] = useState<MenuItem[]>(baseMenuItems);

  // Keep menu structure in sync, then overlay bookmarks from cache/API.
  useEffect(() => {
    const applyBookmarksToMenu = (bookmarks: { title: string; to: string; fullPath?: string | null }[]) => {
      const bookmarksWithIcons: MenuItem[] = bookmarks.map((entry) => bookmarkToMenuItem(entry, baseMenuItems));

      const updatedMenuItems = baseMenuItems.map((item) => {
        if (item.name === 'Bookmarks') {
          return {
            ...item,
            children: bookmarksWithIcons,
          };
        }
        return item;
      });

      setMenuItemsWithBookmarks(updatedMenuItems);
    };

    // Always sync base structure first (Bookmarks group must exist even when empty).
    const cachedBookmarks = getCachedBookmarks();
    if (cachedBookmarks.length > 0) {
      applyBookmarksToMenu(cachedBookmarks);
    } else {
      setMenuItemsWithBookmarks(baseMenuItems);
    }

    const updateBookmarks = async () => {
      try {
        const bookmarks = await getBookmarks();
        applyBookmarksToMenu(bookmarks);
      } catch (error) {
        console.error('Error loading bookmarks:', error);
      }
    };

    updateBookmarks();

    const handleBookmarksUpdate = () => {
      updateBookmarks();
    };

    window.addEventListener('bookmarksUpdated', handleBookmarksUpdate);

    return () => {
      window.removeEventListener('bookmarksUpdated', handleBookmarksUpdate);
    };
  }, [baseMenuItems, baseMenuItemsKey]);

  const handleBookmarksManagerClose = () => {
    // Closing the manager should be instant; actual bookmark changes
    // already dispatch 'bookmarksUpdated' from the bookmarks utilities.
    setBookmarksManagerOpen(false);
  };

  const toggleCollapsed = () => {
    if (isRail) return;
    setSidebarCollapsed(!collapsed);
    closeDrawer();
  };

  const openDrawer = (level: 'level2' | 'level3' | 'level4', data: MenuItem) => {
    if (!collapsed) return;
    
    if (activeDrawer && level !== activeDrawer) {
      setDrawerHistory(prev => [...prev, { level: activeDrawer, data: drawerData[activeDrawer]! }]);
    }
    
    setDrawerData({
      level2: null,
      level3: null,
      level4: null,
      [level]: data,
    });
    setActiveDrawer(level);
  };

  const closeDrawer = useCallback(() => {
    setActiveDrawer(null);
    setDrawerData({ level2: null, level3: null, level4: null });
    setDrawerHistory([]);
  }, []);

  useEffect(() => {
    if (!collapsed || activeDrawer == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [collapsed, activeDrawer, closeDrawer]);

  const goBack = () => {
    if (drawerHistory.length > 0) {
      const previousDrawer = drawerHistory[drawerHistory.length - 1];
      setDrawerHistory(prev => prev.slice(0, -1));
      setDrawerData({
        level2: null,
        level3: null,
        level4: null,
        [previousDrawer.level as 'level2' | 'level3' | 'level4']: previousDrawer.data,
      });
      setActiveDrawer(previousDrawer.level as 'level2' | 'level3' | 'level4');
    }
  };

  const handleItemClick = (item: MenuItem, hasSubitems: boolean) => {
    if (!collapsed) return;
  
    // Detect if this is a top-level menu group
    const isTopLevelItem = menuItemsWithBookmarks.some((rootItem) => rootItem === item);

    if (hasSubitems) {
      if (isTopLevelItem) {
        // Always open top-level groups in the first (level2) drawer,
        // so switching between groups doesn't shift drawers further right
        openDrawer('level2', item);
        return;
      }

      // For nested groups, determine which level to open based on current drawer state
      if (!activeDrawer) {
        openDrawer('level2', item);
      } else if (activeDrawer === 'level2') {
        openDrawer('level3', item);
      } else if (activeDrawer === 'level3') {
        openDrawer('level4', item);
      }
    } else if (item.path) {
      navigate(item.path);
      closeDrawer();
      if (isMobileOpen) {
        setIsMobileOpen(false);
      }
    }
  };

  const collapsedWidth = '64px';
  const expandedWidth = '256px'; // w-64

  const [activeTab, setActiveTab] = useState<'All' | 'Dashboard' | 'Work' | 'Reference' | 'System'>('Dashboard');
  const [activeWorkspace, setActiveWorkspace] = useState<'All' | 'Work' | 'Reference' | 'System'>('Work');

  const filterRootByTab = (tab: typeof activeTab, items: MenuItem[]): MenuItem[] => {
    if (tab === 'All') return items;
    const tabMap: Record<typeof activeTab, string[]> = {
      All: [],
      Dashboard: ['Dashboard', 'Bookmarks', 'Supplies', 'Spares'],
      Work: ['Bookmarks', 'Dashboard', 'Supplies', 'Spares'],
      Reference: ['Reference data'],
      System: ['System'],
    };
    let names = tabMap[tab] ?? [];
    if (tab === 'Dashboard' && items.some((i) => i.name === 'Supplies')) {
      names = [...names, 'Supplies'];
    }
    if (tab === 'Dashboard' && items.some((i) => i.name === 'Spares')) {
      names = [...names, 'Spares'];
    }
    if (names.length === 0) return items;
    return items.filter((item) => item.sectionLabel || names.includes(item.name));
  };

  const filterRootByWorkspace = (workspace: typeof activeWorkspace, items: MenuItem[]): MenuItem[] => {
    if (workspace === 'All') return items;
    const wsMap: Record<typeof activeWorkspace, string[]> = {
      All: [],
      Work: ['Bookmarks', 'Dashboard', 'Supplies', 'Spares'],
      Reference: ['Reference data'],
      System: ['System'],
    };
    const names = wsMap[workspace] ?? [];
    if (names.length === 0) return items;
    return items.filter((item) => item.sectionLabel || names.includes(item.name));
  };

  const orderBookmarksAfterDashboard = (items: MenuItem[]): MenuItem[] => {
    const bookmarksItem = items.find((item) => item.name === 'Bookmarks');
    if (!bookmarksItem) return items;
    const rest = items.filter((item) => item.name !== 'Bookmarks');
    const dashboardIdx = rest.findIndex((item) => item.name === 'Dashboard');
    if (dashboardIdx < 0) {
      return [bookmarksItem, ...rest];
    }
    const ordered = [...rest];
    ordered.splice(dashboardIdx + 1, 0, bookmarksItem);
    return ordered;
  };

  const rootMenuItems = useMemo(() => {
    let rootItems = menuItemsWithBookmarks;

    if (sidebarStyle === 'tabs') {
      rootItems = filterRootByTab(activeTab, rootItems);
    } else if (sidebarStyle === 'workspaces') {
      rootItems = filterRootByWorkspace(activeWorkspace, rootItems);
    }

    rootItems = rootItems.filter(
      (item) => item.name !== 'Bookmarks' || showBookmarksInSidebar,
    );

    if (showBookmarksInSidebar) {
      rootItems = orderBookmarksAfterDashboard(rootItems);
    }

    return rootItems;
  }, [
    menuItemsWithBookmarks,
    sidebarStyle,
    activeTab,
    activeWorkspace,
    showBookmarksInSidebar,
  ]);

  const sidebarDisplayName =
    user?.userDisplayName?.trim() ||
    [user?.userFirstName, user?.userLastName].filter(Boolean).join(' ') ||
    user?.userEmail ||
    '';
  const sidebarInitial = sidebarDisplayName ? sidebarDisplayName.charAt(0).toUpperCase() : 'U';

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50"
          style={zStyle(Z_INDEX.mobileSidebarOverlay)}
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={asideRef}
        className={cn(
          'fixed lg:static inset-y-0 left-0 transform transition-all duration-300 ease-in-out border-r',
          colors.sidebarBackground,
          colors.border,
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        style={{
          width: collapsed ? collapsedWidth : expandedWidth,
          ...zStyle(Z_INDEX.shellSidebar),
        }}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Brand */}
          <div className={cn('flex items-center justify-between h-16 border-b', colors.border, collapsed ? 'px-2' : 'px-6')}>
            {!collapsed && (
              <div className="flex items-center space-x-3">
                <img
                  src={`${process.env.PUBLIC_URL}/atlas-logo.svg`}
                  alt=""
                  className="h-10 w-10 rounded-lg shadow-sm shadow-black/20"
                />
                <div>
                  <h2 className={cn('text-lg font-bold', colors.sidebarTextHover)}>ATLAS SCMS</h2>
                </div>
              </div>
            )}
            {collapsed && (
              <img
                src={`${process.env.PUBLIC_URL}/atlas-logo.svg`}
                alt="ATLAS"
                className="h-9 w-9 rounded-lg"
              />
            )}
            <div className="flex items-center gap-2">
              {/* Collapse Toggle Button (only for classic-style layouts) */}
              {sidebarStyle === 'classic' && (
                <button
                  onClick={toggleCollapsed}
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 border-2',
                    colors.sidebarBackground,
                    colors.border,
                    colors.sidebarText,
                    colors.sidebarItemHover
                  )}
                  title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {collapsed ? (
                    <ChevronRightIcon className="w-4 h-4" />
                  ) : (
                    <ChevronLeftIcon className="w-4 h-4" />
                  )}
                </button>
              )}
              {/* Mobile close button */}
              <button
                onClick={() => setIsMobileOpen(false)}
                className={cn('lg:hidden rounded-lg p-1', colors.sidebarText, colors.sidebarItemHover)}
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <SidebarExpandedContext.Provider value={sidebarExpandedValue}>
          <nav className={cn('flex-1 overflow-y-auto', collapsed ? 'px-2 py-6' : 'px-4 py-6')}>
            {/* Tabs header for 'tabs' style */}
            {isTabs && (
              <div className="mb-3">
                <div className="flex flex-wrap items-center gap-1 rounded-xl bg-black/5 dark:bg-white/5 p-1">
                  {(['All', 'Dashboard', 'Work', 'Reference', 'System'] as const).map((tab) => {
                    const isActive = activeTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveTab(tab)}
                        className={cn(
                          'px-3 py-1.5 text-xs font-medium rounded-full transition-colors',
                          isActive ? colors.surface : colors.surfaceHover,
                          isActive ? colors.text : colors.textMuted,
                          tab === 'All' ? 'hidden lg:inline-flex' : 'inline-flex'
                        )}
                      >
                        {tab}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Workspaces header for 'workspaces' style */}
            {isWorkspaces && (
              <div className="mb-3">
                <div className="flex flex-wrap items-center gap-1 rounded-xl bg-black/5 dark:bg-white/5 p-1">
                  {(['All', 'Work', 'Reference', 'System'] as const).map((ws) => {
                    const isActive = activeWorkspace === ws;
                    return (
                      <button
                        key={ws}
                        type="button"
                        onClick={() => setActiveWorkspace(ws)}
                        className={cn(
                          'px-3 py-1.5 text-xs font-medium rounded-full transition-colors',
                          isActive ? colors.surface : colors.surfaceHover,
                          isActive ? colors.text : colors.textMuted,
                          ws === 'All' ? 'hidden lg:inline-flex' : 'inline-flex'
                        )}
                      >
                        {ws}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-1">
              {rootMenuItems.map((item, index) => {
                if (item.sectionLabel) {
                  return (
                    <MenuItemComponent
                      key={`section-${item.sectionLabel}-${index}`}
                      item={item}
                      level={0}
                      location={location}
                      isMobileOpen={isMobileOpen}
                      setIsMobileOpen={setIsMobileOpen}
                      collapsed={collapsed}
                      onItemClick={handleItemClick}
                      viewPreferences={viewPreferences || {}}
                      mutateViewPreferences={mutateViewPreferences}
                    />
                  );
                }
                const isBookmarks = item.name === 'Bookmarks';
                return (
                  <MenuItemComponent
                    key={item.path || `menu-item-${index}`}
                    item={item}
                    level={0}
                    location={location}
                    isMobileOpen={isMobileOpen}
                    setIsMobileOpen={setIsMobileOpen}
                    isBookmarks={isBookmarks}
                    onManageBookmarks={isBookmarks ? () => setBookmarksManagerOpen(true) : undefined}
                    collapsed={collapsed}
                    onItemClick={handleItemClick}
                    viewPreferences={viewPreferences || {}}
                    mutateViewPreferences={mutateViewPreferences}
                  />
                );
              })}
            </div>
          </nav>
          </SidebarExpandedContext.Provider>

          {/* User panel at bottom */}
          {user && (
            <div className={cn('mt-auto border-t', colors.border, collapsed ? 'px-2 py-3' : 'px-4 py-4')}>
              <div className={cn('flex items-center gap-3', collapsed && 'justify-center')}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500/90 to-purple-600/90 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-xs">
                    {sidebarInitial}
                  </span>
                </div>
                {!collapsed && (
                  <div className="min-w-0">
                    <p className={cn('text-sm font-medium truncate', colors.sidebarText)}>
                      {sidebarDisplayName || 'User'}
                    </p>
                    <p className={cn('text-xs truncate', colors.sidebarText)}>
                      {user.userEmail}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </aside>

      {/* Drawers for collapsed mode: one flyout, same anchor as the rail (measured sidebar right edge) */}
      {collapsed && (
        <Drawer
          isOpen={activeDrawer !== null}
          onClose={closeDrawer}
          item={
            activeDrawer
              ? (drawerData[activeDrawer] || { name: '', children: [] })
              : { name: '', children: [] }
          }
          level={activeDrawer || 'level2'}
          leftPosition={railRightPx}
          onItemClick={handleItemClick}
          onBack={goBack}
          showBack={drawerHistory.length > 0}
          viewPreferences={viewPreferences || {}}
        />
      )}

      {/* Bookmarks Manager Modal */}
      <BookmarksManager
        open={bookmarksManagerOpen}
        onClose={handleBookmarksManagerClose}
      />
    </>
  );
};

export default Sidebar;
